export * from './first-party-mcp-connectors';
export * from './google-calendar-connect';
export const SCHEDULING_ASSISTANT_USER_APP_DEF_ID = '4886d214-d965-4e97-9f49-51c7a80a345f';
export const SCHEDULING_ASSISTANT_UOU_APP_DEF_ID = 'f5b4ce00-4f7b-4066-a607-0277d16780b8';

// DevCenter component (type=WIX_AI_GATEWAY_CALLBACK) that routes WixAiGateway async
// completions back to orion-harness-poc via the SPI HandleResult callback. The appId
// is SCHEDULING_ASSISTANT_UOU_APP_DEF_ID above.
export const ORION_HARNESS_WIX_AI_GATEWAY_CALLBACK_COMPONENT_ID = '34b09d1f-1e9c-4a9a-8084-aef76c3ecb98';
export const WIX_WHAAAT_METASITE_ID = 'cc119d2c-2d63-4cf0-9531-b07ef0728c91';
export const WIX_WELLNESS_METASITE_ID = 'b1e78495-753d-4301-963d-2a6dec50fb8f';
export const BOOKINGS_APP_DEF_ID = '13d21c63-b5ec-5912-8397-c3a5ddb27a97';
export const BM_STANDALONE_APP_BASE_URL = 'https://manage.wix.com/orion';
export const BM_CONNECT_CALENDAR_SUCCESS_PAGE_URL = `${BM_STANDALONE_APP_BASE_URL}/connect/success`;
export const BM_CONNECT_CALENDAR_FAILURE_PAGE_URL = `${BM_STANDALONE_APP_BASE_URL}/connect/failure`;
export const UOU_CHAT_APP_BASE_URL = 'https://chat.nownia.com';
export const SYMPHONY_WEB_BASE_URL = 'https://symphony.wix.com';
// The mobile app's universal-link host: an `https://symphony.wix.app/…` URL opens the corresponding app
// screen when tapped in a chat (the app derives its internal `orion://` scheme from it at render time).
// See the deep-link table in skills/app-guide.ts, which pins the same host as part of the contract.
export const SYMPHONY_APP_BASE_URL = 'https://symphony.wix.app';
/**
 * The two spellings ONE entity's detail has: the app's universal-link path, and orion-web's own route.
 * They differ — web has no routine-detail screen, so a routine opens as an expanded card on the list — and
 * `entityDeeplink` carries BOTH in a single URL rather than picking one per surface.
 *
 * The `web` values are pinned against orion-web's resolver, its page files and its redirect table by
 * `orion-web/test/deeplink-surfaces.test.ts`, so a route rename there fails a test rather than silently
 * minting a link that opens the wrong thing.
 */
export const DEEPLINK_PATHS = {
  task: {
    app: 'task/{id}',
    web: 'tasks/{id}'
  },
  routine: {
    app: 'pulse/{id}',
    web: 'pulses?openId={id}'
  }
};
/**
 * The surface-specific path (no leading slash) for one entity, with `id` substituted and encoded.
 *
 * The encoding holds within OUR two consumers — web's renderer resolves the pathname itself, and an id
 * carrying `&` would otherwise split the query it lands in. It does NOT survive the links service: `d` is
 * decoded twice there, so `openId=a%2526b` arrives as `openId=a&b` and the id truncates (measured against
 * `symphony.wix.app` per user-agent). Ids are server-assigned GUIDs, so nothing live reaches that; the
 * encoding is here so a malformed id degrades to a wrong-id link rather than a broken URL, not because a
 * reserved character round-trips.
 */
export function deeplinkPath(entity, surface, id) {
  return DEEPLINK_PATHS[entity][surface].replace('{id}', encodeURIComponent(id));
}

/**
 * ONE link to an entity, correct wherever it is opened — no per-surface choice, because the surface a
 * conversation happens on is not the surface the link gets opened on. A chat is visible on both a desktop
 * and a phone, and a link gets copied, pasted and reopened long after it was sent.
 *
 * The links service in front of `symphony.wix.app` resolves it per user-agent, and BOTH parameters are
 * required to get that right (measured per user-agent, not reasoned about):
 *
 * - `d` is the desktop destination, but the service applies it on EVERY platform
 *   (`mobileUrl.filter(isMobileOS).orElse(desktopUrl)`), so on its own it would capture phones too and
 *   send them to the web instead of the app.
 * - `s` (`suggestMobile`) gates the store-link resolver that runs ahead of `d`
 *   (`if (suggestMobile && isMobileOS(userAgent))`), which is what keeps `d` desktop-only.
 *
 * So: a phone with the app opens the app on the entity; a phone without it goes to the store with the
 * deferred `?deeplink=` intact; a desktop lands on the web page. Inside web chat the renderer reads the
 * pathname and ignores the query, so a click is an in-app navigation rather than a page load.
 *
 * The same trap is written up in `native-apps/GOTCHAS.md`: a link change must be tested with a MOBILE
 * user-agent and the app uninstalled, because the installed-app path keeps working and hides the failure.
 *
 * The same two parameters are hand-built in four other places (both native referral builders, orion-web's
 * `referral-share-link.ts` and the server's native-data endpoint). New producers should call this rather
 * than copy the shape a sixth time.
 */
export function appLinkWithDesktopFallback(appPath, webPath) {
  const web = `${SYMPHONY_WEB_BASE_URL}/${webPath}`;
  return `${SYMPHONY_APP_BASE_URL}/${appPath}?s=true&d=${encodeURIComponent(web)}`;
}
export function entityDeeplink(entity, id) {
  return appLinkWithDesktopFallback(deeplinkPath(entity, 'app', id), deeplinkPath(entity, 'web', id));
}
export const INDIVIDUAL_CONNECT_CALENDAR_SUCCESS_PAGE_URL = `${SYMPHONY_WEB_BASE_URL}/connect/success`;
export const INDIVIDUAL_CONNECT_CALENDAR_FAILURE_PAGE_URL = `${SYMPHONY_WEB_BASE_URL}/connect/failure`;
export const UOU_CHAT_CONNECT_CALENDAR_SUCCESS_PAGE_URL = `${UOU_CHAT_APP_BASE_URL}/connect/success`;
export const UOU_CHAT_CONNECT_CALENDAR_FAILURE_PAGE_URL = `${UOU_CHAT_APP_BASE_URL}/connect/failure`;
// Brand identity axes, kept separate on purpose (see resolveAgentIdentity): the PRODUCT/app name
// ("Symphony") and the primary AGENT's name ("The Maestro") were one word ("Orion") and split at the
// Symphony rebrand. The rebrand has GA'd and the `symphonyRebrand` Petri experiment was removed, so these
// resolve to the Symphony-era values unconditionally. A per-user override of the agent name lives in the
// Recipient entity (agentName field).
export const MAESTRO_ASSISTANT_NAME = 'The Maestro';
// Female presentation of the primary agent's name. The Maestro is the only assistant name that genders
// (The Maestro → The Maestra); the persona archetypes (Guide/Chief/Maverick) are gender-neutral.
export const MAESTRA_ASSISTANT_NAME = 'The Maestra';
export const SYMPHONY_PRODUCT_NAME = 'Symphony';
export const STATIC_IMAGE_DOMAIN = 'https://static.wixstatic.com';
export const UOU_CONVERSATION_ORIGIN = 'uou-scheduling-assistant';
export const INDIVIDUAL_CONVERSATION_ORIGIN = 'individual-scheduling-assistant';
export const MEETING_INVITATION_SENT_AUTOMATION_ID = '707be516-5a63-435a-b020-53b8e5542eec';
export const MEETING_REMINDERS_AUTOMATION_ID = '707be516-5a63-435a-b020-53b8e5542eec';
export const UOU_UNIFIED_ASSISTANT_ID = '816d02ea-0c56-4fe7-8850-fc420383a249';
export const INDIVIDUAL_UNIFIED_ASSISTANT_ID = '5df736a5-c235-4648-8eb1-df868689c9e4';
export const INDIVIDUAL_ONBOARDING_UNIFIED_ASSISTANT_ID = 'cb4b460a-b2cb-41e7-8da1-59f89c07d5fd';
export const INDIVIDUAL_KNOWLEDGE_UNIFIED_ASSISTANT_ID = 'e3af2dde-ae3a-4d5e-bb71-6a17631ac5f7';
export const UOU_SCHEDULING_ASSISTANT_HEADLESS_CLIENT_ID = '76e857b7-b04e-42b6-9e03-dbc1e7bc7b40';
export const ASSISTANT_NAME_CHAR_LIMIT = 15;
export const MAX_ATTACHMENT_SIZE_BYTES = 5 * 1024 * 1024;

/**
 * Video's own per-file ceiling, deliberately NOT `MAX_ATTACHMENT_SIZE_BYTES`.
 *
 * That 5 MB is the tightest AI-provider bound (Anthropic's per-image limit) and applies because an
 * image or a PDF is sent INTO the model. A video never is — every mapper resolves it to a text note
 * instead (`resolve-attachment-content.ts` short-circuits `video/*` before it is even fetched), so it
 * is carried purely as a URL in the message's `<metadata>` for the agent to hand to a connector.
 * Inheriting the gateway's cap would have limited uploads to ~15 seconds of phone video for a bound
 * nothing on this path is subject to.
 *
 * 200 MB sits under Instagram Reels' 300 MB ceiling and far under TikTok's 4 GB.
 *
 * ⚠️ Above ~25 MB the byte-staging path to Composio (`MAX_STAGED_BYTES` in
 * `tools/composio/file-uploadable.ts`) will refuse the file, so a large video is only postable through
 * a connector that takes a public URL and fetches it itself (Meta does; TikTok's URL path needs domain
 * verification we do not have). Raising that staging cap is a separate memory question — see its own
 * comment, which measures ~12x the file in RSS.
 */
export const MAX_VIDEO_ATTACHMENT_SIZE_BYTES = 200 * 1024 * 1024;

/**
 * Video containers a user may attach. Explicit rather than a `video/*` prefix test: these are the three
 * the destinations actually take (and the three `rehost-reply-assets` already maps), so an `.avi` fails
 * at the picker with a clear reason instead of at the post, after an upload the user waited for.
 */
export const VIDEO_ATTACHMENT_MIMES = ['video/mp4', 'video/quicktime', 'video/webm'];

/** True for a video container we accept. Tolerant of a `;codecs=…` parameter, which pickers do append. */
export function isVideoAttachmentMime(mime) {
  return VIDEO_ATTACHMENT_MIMES.includes((mime ?? '').toLowerCase().split(';')[0].trim());
}

/** The per-file ceiling for a given mime — video gets its own, everything else the provider bound. */
export function maxAttachmentSizeBytes(mime) {
  return isVideoAttachmentMime(mime) ? MAX_VIDEO_ATTACHMENT_SIZE_BYTES : MAX_ATTACHMENT_SIZE_BYTES;
}
export const SEND_MESSAGE_TIME_BUDGET = 360_000;
export const ABORT_MESSAGE_TIME_BUDGET = 30_000;
export const BRAIN_SIMULATION_TIME_BUDGET = 120_000;
// The mailbox we hand out and receive on. email-integration watches two Gmail mailboxes and this is
// its `defaultOutbound` one — the From address on every new thread, so it is also the address a
// recipient replies to. `orion@wix.com` is still configured there as the legacy mailbox (it owns all
// pre-rename threads and their per-mailbox Gmail threadIds) but must never be advertised again.
export const SYMPHONY_MAILBOX_ADDRESS = 'symphony@wix.com';
export const INDIVIDUAL_SIMULATION_META_SITE_IDS = ['cce9739c-9ab0-4e26-9930-ffb3800c04b9', '3d33d283-2e47-4b9b-b4dd-4ea76cf0853d'];
export const UOU_ASSISTANT_REFERENCE = {
  type: 'unified-assistant',
  unifiedAssistantId: UOU_UNIFIED_ASSISTANT_ID
};
export const INDIVIDUAL_ASSISTANT_REFERENCE = {
  type: 'unified-assistant',
  unifiedAssistantId: INDIVIDUAL_UNIFIED_ASSISTANT_ID
};
export const SYMPHONY_WHATSAPP_WABA_ID = '742949560008212';
export const SYMPHONY_WHATSAPP_PHONE_NUMBER_ID = '753186627877106';
export const SYMPHONY_WHATSAPP_INBOUND_NUMBER = '15188556881';
export const SYMPHONY_SMS_ACCOUNT_ID = '00000000-0000-0000-0000-000000000001';
export const SYMPHONY_SMS_INBOUND_NUMBER = '+18888953379';
// The SMS provider's hard ceiling on a message body: `SmsProviderService/SendSms` rejects anything
// longer with `INVALID_ARGUMENT: body has size N, expected 1530 or less`, and the send simply never
// happens. Enforced at the SmsClient boundary (the one place every send funnels through) rather than
// per-call-site, so the intro template, the opt-in/opt-out confirmations and the assistant's own
// replies are all covered by construction.
export const SMS_BODY_MAX_CHARS = 1530;
// The seeded, non-deletable "daily agent meeting" (morning brief) PulseIdentity. It is a SITE-LEVEL
// routine with an empty workspaceId, so a workspace-scoped routines query never matches it — consumers
// that want it in a workspace list must merge it in by this id. Same value as orion-web's
// lib/brief-state.ts BRIEF_PULSE_IDENTITY_ID and orion-live-server's DAILY_MEETING_PULSE_IDENTITY_ID.
export const BRIEF_PULSE_IDENTITY_ID = '00000000-0000-0000-0000-000000000002';

// Browserbase constants
export const BROWSERBASE_CONSTANTS = {
  STAGEHAND: {
    ENV: 'BROWSERBASE',
    MODEL_NAME: 'claude-3-7-sonnet-latest',
    HEADLESS: true,
    ENABLE_CACHING: true
  },
  SCREENSHOT: {
    TYPE: 'png',
    ENCODING: 'base64'
  }
};
export const FEEDBACK_FORM_ID = '910cc899-42f8-4810-81c9-3681ad488bc8';
export const LANDING_PAGE_WEBSITE_MSID = '70ab3653-f410-455f-a7f6-0e39c48963ab';

// Default categories for task digest
export const DEFAULT_CATEGORIES = ['Work', 'Errands', 'Family', 'Health', 'Social', 'Travel', 'Events & Culture', 'Finance', 'Learning & Enrichment'];

/**
 * The languages Symphony supports, as bare ISO-639-1 codes (the shape Site Properties stores).
 *
 * This is the same 37-locale catalog every client surface already ships: `SUPPORTED_LOCALES` in
 * orion-web's `lib/i18n.ts`, `Loc.supported` in the iOS and Android apps, and the `messages_<code>.json`
 * bundles in ai-scheduling-assistant-localization. Those copies can't be shared — orion-web is a private
 * Next.js app with no exports field, and the native lists are Swift/Kotlin — so this is the importable
 * one for the server-side packages. Adding or dropping a locale means updating the client lists too.
 */
export const SUPPORTED_LANGUAGE_CODES = ['ar', 'bg', 'ca', 'cs', 'da', 'de', 'el', 'en', 'es', 'fi', 'fr', 'he', 'hi', 'hr', 'hu', 'id', 'it', 'ja', 'ko', 'lt', 'lv', 'ms', 'nl', 'no', 'pl', 'pt', 'ro', 'ru', 'sk', 'sl', 'sv', 'th', 'tl', 'tr', 'uk', 'vi', 'zh'];
export function isSupportedLanguageCode(code) {
  return SUPPORTED_LANGUAGE_CODES.includes(code);
}
//# sourceMappingURL=index.js.map